import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Star, Check, Shield, TrendingUp, BarChart3, Link2, X, FileText } from 'lucide-react';

import {
  getProductById,
  getFeatureByProduct,
  getPlansByProduct,
  getSpecificationsByProduct,
  getReviewsByProduct,
  getSupportByProduct
} from '../../service/product.service';

import '../Common/AllCalculator.css';
import MainFooter from '../Common/MainFooter';

// ============= UTILITY FUNCTIONS =============

const normalizeArrayResponse = (data) => {
  if (Array.isArray(data)) return data;
  if (data?.data && Array.isArray(data.data)) return data.data;
  return [];
};

const getReviewField = (review, fieldNames, defaultValue = null) => {
  for (const field of fieldNames) {
    if (review[field]) return review[field];
  }
  return defaultValue;
};

const processSpecifications = (specsArray) => {
  const specsObj = {};
  specsArray.forEach(spec => {
    const key = spec.specificationName;
    const value = spec.specificationDetails;
    if (key && value) {
      specsObj[key] = value;
    }
  });
  return specsObj;
};

const processSupportData = (supportArray) => {
  const supportObj = {};
  supportArray.forEach(sup => {
    const key = sup.supportName;
    const value = sup.supportDetails;
    if (key && value) {
      supportObj[key] = value;
    }
  });
  return supportObj;
};

// ============= DEMO MODAL COMPONENT =============

const DemoRequestModal = ({ isOpen, onClose, productName }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    userType: '',
    mobileNumber: '',
    email: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.userType) {
      newErrors.userType = 'Please select a user type';
    }

    if (!formData.mobileNumber.trim()) {
      newErrors.mobileNumber = 'Mobile number is required';
    } else if (!/^\+?[\d\s-]{10,}$/.test(formData.mobileNumber.trim())) {
      newErrors.mobileNumber = 'Please enter a valid mobile number';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    // Prepare payload with Web3Forms access key
    const payload = {
      ...formData,
      productName: productName,
      access_key: 'ffe9b71b-4e54-41ed-bb2a-c52a8b9d9ce4'
    };

    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      }).then((r) => r.json());

      if (res.success) {
        console.log('Demo request submitted successfully:', {
          ...formData,
          productName
        });

        setSubmitSuccess(true);

        // Reset form after 2 seconds and close modal
        setTimeout(() => {
          setFormData({
            firstName: '',
            userType: '',
            mobileNumber: '',
            email: '',
            message: ''
          });
          setSubmitSuccess(false);
          onClose();
        }, 2000);
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Error submitting demo request:', error);
      setErrors({ submit: '❌ Something went wrong. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({
        firstName: '',
        userType: '',
        mobileNumber: '',
        email: '',
        message: ''
      });
      setErrors({});
      setSubmitSuccess(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="demo-modal-overlay" onClick={handleClose}>
      <div className="demo-modal-container" onClick={e => e.stopPropagation()}>
        <button
          className="demo-modal-close"
          onClick={handleClose}
          disabled={isSubmitting}
          aria-label="Close modal"
        >
          <X size={24} />
        </button>

        <div className="demo-modal-header">
          <h2>Request Free Demo</h2>
          <p>Fill in your details and we'll get back to you shortly</p>
        </div>

        {submitSuccess ? (
          <div className="demo-success-message">
            <div className="success-icon">
              <Check size={48} />
            </div>
            <h3>Request Submitted Successfully!</h3>
            <p>✅ Thank you for requesting a demo! We will get back to you soon.</p>
          </div>
        ) : (
          <form className="demo-modal-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="firstName">
                First Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className={errors.firstName ? 'error' : ''}
                placeholder="Enter your first name"
                disabled={isSubmitting}
              />
              {errors.firstName && (
                <span className="error-message">{errors.firstName}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="userType">
                User Type <span className="required">*</span>
              </label>
              <select
                id="userType"
                name="userType"
                value={formData.userType}
                onChange={handleChange}
                className={errors.userType ? 'error' : ''}
                disabled={isSubmitting}
              >
                <option value="">Select Type</option>
                <option value="Individual">Individual</option>
                <option value="Organisation">Organisation</option>
              </select>
              {errors.userType && (
                <span className="error-message">{errors.userType}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="mobileNumber">
                Mobile Number <span className="required">*</span>
              </label>
              <input
                type="tel"
                id="mobileNumber"
                name="mobileNumber"
                value={formData.mobileNumber}
                onChange={handleChange}
                className={errors.mobileNumber ? 'error' : ''}
                placeholder="Enter your mobile number"
                disabled={isSubmitting}
              />
              {errors.mobileNumber && (
                <span className="error-message">{errors.mobileNumber}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="email">
                Email ID <span className="required">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={errors.email ? 'error' : ''}
                placeholder="Enter your email address"
                disabled={isSubmitting}
              />
              {errors.email && (
                <span className="error-message">{errors.email}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="message">Message</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Tell us about your requirements (optional)"
                rows="4"
                disabled={isSubmitting}
              />
            </div>

            {errors.submit && (
              <div className="submit-error">{errors.submit}</div>
            )}

            <button
              type="submit"
              className="demo-submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Request Demo'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

// ============= COMPONENT SECTIONS =============

const LoadingSpinner = () => (
  <div className="loading-container">
    <div className="loading-content">
      <div className="loading-spinner"></div>
      <p className="loading-text">Loading product details...</p>
    </div>
  </div>
);

const ProductNotFound = () => (
  <div className="loading-container">
    <div className="loading-content">
      <p className="loading-text">Product not found</p>
      <Link to="/" className="btn btn-primary mt-3">
        Back to Home
      </Link>
    </div>
  </div>
);

const HeroSection = ({ product, reviewsCount, scrollToSection, onDemoClick }) => {
  const handleImageError = (e) => {
    e.target.onerror = null;
    e.target.src = 'https://via.placeholder.com/200x200?text=Product+Logo';
  };

  return (
    <div className="hero-section">
      <div className="container">
        <div className="hero-content">
          <div className="product-logo-wrapper">
            <img
              src={product.productLogo || product.logo || 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=200&h=200&fit=crop'}
              alt={`${product.productName} logo`}
              className="product-logo"
              onError={handleImageError}
              loading="lazy"
            />
          </div>

          <div className="product-info">
            <div className="product-badge-wrapper">
              <span className="product-badge">{product.badge || 'Verified'}</span>
            </div>

            <h1 className="product-title">{product.productName}</h1>
            <p className="product-vendor">by {product.vendor || 'Elexyt Technologies'}</p>
            <p className="product-tagline">{product.productDescription}</p>

            <div className="rating-section">
              <div className="rating-stars" role="img" aria-label={`Rated ${product.rating || 4.5} out of 5 stars`}>
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    size={20}
                    className={i < Math.floor(product.rating || 4.5) ? 'star-filled' : 'star-empty'}
                    aria-hidden="true"
                  />
                ))}
              </div>
              <span className="rating-number">{product.rating || 4.5}</span>
              <span className="rating-count">({reviewsCount} reviews)</span>
            </div>

            <div className="cta-buttons">
              <button
                className="btn-primary"
                onClick={() => scrollToSection('pricing')}
                aria-label="View purchase options"
              >
                View Purchase Options
              </button>
              <button
                className="btn-secondary"
                onClick={onDemoClick}
                aria-label="Try free demo"
              >
                Try Free Demo
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const TabsNavigation = ({ scrollToSection }) => {
  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'features', label: 'Features' },
    { id: 'pricing', label: 'Pricing' },
    { id: 'specifications', label: 'Specifications' },
    { id: 'support', label: 'Support' },
    { id: 'reviews', label: 'Reviews' },
    { id: 'legal', label: 'Legal' },
    { id: 'documents', label: 'Documents' }
  ];

  return (
    <div className="tabs-nav">
      <div className="container">
        <nav className="tabs-wrapper" role="navigation" aria-label="Product sections">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => scrollToSection(tab.id)}
              className="tab-button"
              aria-label={`Navigate to ${tab.label} section`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>
    </div>
  );
};

const OverviewSection = ({ product }) => (
  <section id="overview" className="content-section">
    <div className="content-card">
      <h2 className="section-title">Overview</h2>
      <p className="section-text">{product.productDescription}</p>
      {product.productOverview && (
        <p className="section-text">{product.productOverview}</p>
      )}
    </div>

    {product.productHighlights && (
      <div className="content-card">
        <h3 className="subsection-title">Key Highlights</h3>
        <ul className="highlights-list">
          {product.productHighlights.split('\r\n').filter(h => h.trim()).map((highlight, idx) => (
            <li key={idx} className="highlight-item">
              <Check className="check-icon" size={20} aria-hidden="true" />
              <span>{highlight}</span>
            </li>
          ))}
        </ul>
      </div>
    )}
  </section>
);

const FeaturesSection = ({ features, getFeatureIcon }) => (
  <section id="features" className="content-section">
    <div className="content-card">
      <h2 className="section-title">Features</h2>
      {features.length > 0 ? (
        <div className="features-grid">
          {features.map((feature, idx) => (
            <div key={idx} className="feature-card">
              <div className="feature-icon-wrapper" aria-hidden="true">
                {getFeatureIcon(feature.featureIcon)}
              </div>
              <h3 className="feature-title">{feature.featureName}</h3>
              <p className="feature-description">{feature.featureDescription}</p>
            </div>
          ))}
        </div>
      ) : (
        <p className="empty-state">No features available at this time.</p>
      )}
    </div>
  </section>
);

const PricingSection = ({ product, productId, plans, isLoggedIn, navigate }) => {

  const handleGetStarted = () => {
    if (isLoggedIn) {
      navigate('/pricing');
    } else {
      navigate('/login');
    }
  };

  return (
    <section id="pricing" className="content-section">
      <div className="content-card">
        <h2 className="section-title">Pricing Plans</h2>

        {plans.length > 0 ? (
          <div className="pricing-grid">
            {plans.map((plan, idx) => (
              <div
                key={idx}
                className={`pricing-card ${plan.isPopular ? 'popular' : ''}`}
              >
                {plan.isPopular && (
                  <span className="popular-badge">Most Popular</span>
                )}

                <h3 className="plan-name">{plan.planName}</h3>

                <div className="plan-price">
                  {plan.planAmountType === 'D' ? (
                    <>
                      <span className="price-amount">₹{plan.planAmount}</span>
                      <span className="price-period">
                        /{plan.planType === 'M' ? 'month' : 'year'}
                      </span>
                    </>
                  ) : (
                    <span className="price-amount">Custom</span>
                  )}
                </div>

                <ul className="plan-features">
                  {plan.planFeatures &&
                    plan.planFeatures
                      .split('\r\n')
                      .filter(f => f.trim())
                      .map((feature, fIdx) => (
                        <li key={fIdx} className="plan-feature">
                          <Check className="check-icon-small" size={16} />
                          <span>{feature}</span>
                        </li>
                      ))}
                </ul>

                <button
                  className="buy-btn"
                  onClick={() => {
                    navigate('/payment/checkout', {
                      state: {
                        productId: productId,
                        productName: product.productName,
                        selectedPlan: {
                          planName: plan.planName,
                          price: plan.planAmount,
                          duration: plan.planType
                        }
                      }
                    });
                  }}
                >
                  Get Started
                </button>

              </div>
            ))}
          </div>
        ) : (
          <p className="empty-state">
            No pricing plans available. Contact us for custom pricing.
          </p>
        )}
      </div>
    </section>
  );
};


const SpecificationsSection = ({ specifications }) => (
  <section id="specifications" className="content-section">
    <div className="content-card">
      <h2 className="section-title">Technical Specifications</h2>
      {Object.keys(specifications).length > 0 ? (
        <div className="specs-table">
          {Object.entries(specifications).map(([key, value]) => (
            <div key={key} className="spec-row">
              <div className="spec-label">{key}</div>
              <div className="spec-value">{value}</div>
            </div>
          ))}
        </div>
      ) : (
        <p className="empty-state">Technical specifications coming soon.</p>
      )}
    </div>
  </section>
);

const SupportSection = ({ support }) => (
  <section id="support" className="content-section">
    <div className="content-card">
      <h2 className="section-title">Support & Resources</h2>
      {Object.keys(support).length > 0 ? (
        <div className="support-grid">
          {Object.entries(support).map(([key, value]) => (
            <div key={key} className="support-card">
              <h3 className="support-title">{key}</h3>
              <p className="support-description">{value}</p>
            </div>
          ))}
        </div>
      ) : (
        <p className="empty-state">Support information will be available soon.</p>
      )}
    </div>
  </section>
);

const ReviewsSection = ({ reviews }) => (
  <section id="reviews" className="content-section">
    <div className="content-card">
      <h2 className="section-title">Customer Reviews</h2>
      {reviews.length > 0 ? (
        <div className="reviews-list">
          {reviews.map((review, idx) => {
            const authorName = getReviewField(
              review,
              ['review_by', 'reviewBy', 'reviewerName', 'customerName', 'userName', 'user'],
              'Anonymous'
            );
            const rating = getReviewField(
              review,
              ['rating', 'reviewRating', 'starRating'],
              0
            );
            const reviewText = getReviewField(
              review,
              ['reviewText', 'reviewComment', 'comment', 'reviewDescription', 'text'],
              'No review text available'
            );
            const reviewDate = getReviewField(
              review,
              ['reviewDate', 'createdDate', 'createdAt', 'date']
            );

            return (
              <article key={idx} className="review-item">
                <div className="review-header">
                  <span className="review-author">{authorName}</span>
                  <div className="review-rating" role="img" aria-label={`Rated ${rating} out of 5 stars`}>
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        size={16}
                        className={i < Math.floor(rating) ? 'star-filled' : 'star-empty'}
                        aria-hidden="true"
                      />
                    ))}
                  </div>
                </div>
                <p className="review-text">{reviewText}</p>
                {reviewDate && (
                  <time className="review-date" dateTime={reviewDate}>
                    {new Date(reviewDate).toLocaleDateString()}
                  </time>
                )}
              </article>
            );
          })}
        </div>
      ) : (
        <div className="reviews-placeholder">
          <p>No reviews available yet. Be the first to review this product!</p>
        </div>
      )}
    </div>
  </section>
);
  const LegalSection = () => (
  <section id="legal" className="content-section">
    <div className="content-card">
      <h2 className="section-title">Legal Information</h2>
      <div className="legal-content">
       <div style={{
          backgroundColor: '#f8f9fa',
          padding: '1.5rem',
          borderRadius: '8px',
          border: '1px solid #e9ecef'
        }}>
          <ul className="highlights-list" style={{ marginBottom: 0 }}>
            <li className="highlight-item">
              <Check className="check-icon" size={20} aria-hidden="true" />
              <span>Terms and Conditions for product usage</span>
            </li>
            <li className="highlight-item">
              <Check className="check-icon" size={20} aria-hidden="true" />
              <span>Privacy Policy and Data Protection compliance</span>
            </li>
            <li className="highlight-item">
              <Check className="check-icon" size={20} aria-hidden="true" />
              <span>License Agreement details</span>
            </li>
            <li className="highlight-item">
              <Check className="check-icon" size={20} aria-hidden="true" />
              <span>Warranty and Liability disclaimers</span>
            </li>
            <li className="highlight-item">
              <Check className="check-icon" size={20} aria-hidden="true" />
              <span>Compliance certifications (ISO, GDPR, etc.)</span>
            </li>
            <li className="highlight-item">
              <Check className="check-icon" size={20} aria-hidden="true" />
              <span>Refund and Cancellation policies</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </section>
);


const DocumentsSection = () => (
  <section id="documents" className="content-section">
    <div className="content-card">
      <h2 className="section-title">Documents & Resources</h2>
      <div className="documents-content">
        <p className="section-text" style={{ marginBottom: '1.5rem', color: '#6c757d' }}>
          Download product documentation, user guides, and additional resources.
        </p>

        <div style={{
          backgroundColor: '#f8f9fa',
          padding: '1.5rem',
          borderRadius: '8px',
          border: '1px solid #e9ecef',
          marginBottom: '1.5rem'
        }}>
          <h3 className="subsection-title" style={{ fontSize: '1.1rem', marginBottom: '1rem' }}>
            Available Resources:
          </h3>
          <ul className="highlights-list" style={{ marginBottom: 0 }}>
            <li className="highlight-item">
              <Check className="check-icon" size={20} aria-hidden="true" />
              <span>User Manual & Installation Guide</span>
            </li>
            <li className="highlight-item">
              <Check className="check-icon" size={20} aria-hidden="true" />
              <span>API Documentation & Developer Guide</span>
            </li>
            <li className="highlight-item">
              <Check className="check-icon" size={20} aria-hidden="true" />
              <span>Training Materials & Video Tutorials</span>
            </li>
          </ul>
        </div>

        {/* Sample Download Buttons - Ready for dynamic implementation */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '1rem'
        }}>
          <button
            className="btn-secondary"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.5rem'
            }}
            disabled
          >
            <FileText size={18} />
            <span>User Manual (PDF)</span>
          </button>
          <button
            className="btn-secondary"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.5rem'
            }}
            disabled
          >
            <FileText size={18} />
            <span>API Documentation</span>
          </button>
          <button
            className="btn-secondary"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.5rem'
            }}
            disabled
          >
            <FileText size={18} />
            <span>Technical Specs</span>
          </button>
        </div>

        <p style={{
          marginTop: '1rem',
          fontSize: '0.875rem',
          color: '#6c757d',
          fontStyle: 'italic'
        }}>
          Note: Document downloads will be enabled once uploaded to the system
        </p>
      </div>
    </div>
  </section>
);

// ============= MAIN COMPONENT =============

const ProductPageComponent = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [features, setFeatures] = useState([]);
  const [plans, setPlans] = useState([]);
  const [specifications, setSpecifications] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [support, setSupport] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);

  const isLoggedIn = useMemo(() => {
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    const userData = localStorage.getItem('userData') || sessionStorage.getItem('userData');
    return !!(token || userData);
  }, []);

  const getFeatureIcon = useCallback((iconName) => {
    const icons = {
      link: <Link2 className="feature-icon-link" size={24} />,
      trending: <TrendingUp className="feature-icon-trending" size={24} />,
      chart: <BarChart3 className="feature-icon-chart" size={24} />,
      shield: <Shield className="feature-icon-shield" size={24} />,
    };
    return icons[iconName] || icons.link;
  }, []);

  const scrollToSection = useCallback((sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 140;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  }, []);

  const fetchAllData = useCallback(async () => {
    if (!productId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const results = await Promise.allSettled([
        getProductById(productId),
        getFeatureByProduct(productId),
        getPlansByProduct(productId),
        getSpecificationsByProduct(productId),
        getReviewsByProduct(productId),
        getSupportByProduct(productId)
      ]);

      const [productResult, featuresResult, plansResult, specsResult, reviewsResult, supportResult] = results;

      if (productResult.status === 'fulfilled') {
        setProduct(productResult.value);
      }

      if (featuresResult.status === 'fulfilled') {
        setFeatures(normalizeArrayResponse(featuresResult.value));
      }

      if (plansResult.status === 'fulfilled') {
        setPlans(normalizeArrayResponse(plansResult.value));
      }

      if (specsResult.status === 'fulfilled') {
        const specsArray = normalizeArrayResponse(specsResult.value);
        setSpecifications(specsArray);
      }

      if (reviewsResult.status === 'fulfilled') {
        setReviews(normalizeArrayResponse(reviewsResult.value));
      }

      if (supportResult.status === 'fulfilled') {
        const supportArray = normalizeArrayResponse(supportResult.value);
        setSupport(supportArray);
      }

    } catch (err) {
      console.error('Error loading product data:', err);
      setError('Unable to load product data. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchAllData();
  }, [fetchAllData]);

  const processedSpecs = useMemo(() => processSpecifications(specifications), [specifications]);
  const processedSupport = useMemo(() => processSupportData(support), [support]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!product) {
    return <ProductNotFound />;
  }

  return (
    <div className="product-detail-page">
      {error && (
        <div className="alert alert-warning alert-dismissible fade show m-0" role="alert">
          <div className="container">
            <strong>Note:</strong> {error}
          </div>
        </div>
      )}

      <div className="header-nav">
        <div className="container">
          <Link to="/" className="back-button" aria-label="Back to Products">
            <ArrowLeft size={20} aria-hidden="true" />
            <span>Back to Products</span>
          </Link>
        </div>
      </div>

      <HeroSection
        product={product}
        reviewsCount={reviews.length}
        scrollToSection={scrollToSection}
        onDemoClick={() => setIsDemoModalOpen(true)}
      />
      <TabsNavigation scrollToSection={scrollToSection} />

      <div className="content-area">
        <div className="container" style={{ maxWidth: '1400px' }}>
          <div className="content-grid" style={{ display: 'grid', gridTemplateColumns: '55% 42%', gap: '3%', alignItems: 'start' }}>
            <main className="main-content">
              <OverviewSection product={product} />
              <FeaturesSection features={features} getFeatureIcon={getFeatureIcon} />
              <PricingSection product={product} plans={plans} productId={productId} isLoggedIn={isLoggedIn} navigate={navigate} />
              <SpecificationsSection specifications={processedSpecs} />
              <SupportSection support={processedSupport} />
              <ReviewsSection reviews={reviews} />
              <LegalSection />         
              <DocumentsSection />     
            </main>

            <aside
              className="video-sidebar"
              style={{
                position: 'sticky',
                top: '140px',
                width: '100%'
              }}
            >
              <div
                style={{
                  backgroundColor: '#ffffff',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  border: '1px solid #e9ecef',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.06)'
                }}
              >
                <h3
                  style={{
                    fontSize: '1.25rem',
                    fontWeight: '600',
                    marginBottom: '1rem',
                    color: '#212529'
                  }}
                >
                  Product Demo
                </h3>

                {/* FINAL FIXED VIDEO WRAPPER */}
                <div
                  style={{
                    position: 'relative',
                    width: '100%',
                    paddingBottom: '56.25%', // 16:9 Ratio
                    borderRadius: '12px',
                    overflow: 'hidden',
                    backgroundColor: '#000'
                  }}
                >
                  <iframe
                    src="https://www.youtube.com/embed/1F-G3ROQqsA"
                    title="YouTube video player"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%'
                    }}
                  ></iframe>
                </div>

                <p
                  style={{
                    marginTop: '1rem',
                    fontSize: '0.875rem',
                    color: '#6c757d',
                    lineHeight: '1.5'
                  }}
                >
                  Watch our product demonstration to see how it works
                </p>
              </div>
            </aside>

          </div>
        </div>
      </div>

      <DemoRequestModal
        isOpen={isDemoModalOpen}
        onClose={() => setIsDemoModalOpen(false)}
        productName={product?.productName}
      />

      <MainFooter />
    </div>
  );
};

export default ProductPageComponent;